/**
 * Resume Checklist Selector
 * ─────────────────────────
 * Pure derivation for the Installer Portal "Resume Checklist" shortcut.
 *
 * Definition of a resumable item:
 *   - Job is assigned to this contractor (matchKey/contractorId)
 *   - Job is scheduled for today (scheduledInstallDate === todayIso)
 *   - A JobSpecExecution exists for the job
 *   - Execution is not fully complete (!allPhasesComplete)
 *
 * Read-only. No writers. No mutations.
 */
import type { Job } from "@/data/types";
import type { JobSpecExecution, PhaseExecution } from "@/data/jobSpecExecution";
import { filterJobsForContractor } from "@/data/portal/contractorPortalSelectors";

export interface ResumableChecklist {
  jobId: string;
  customerName: string;
  addressCity: string;
  currentPhase: string;
  href: string;
}

function deriveCurrentPhase(exec: JobSpecExecution): string {
  const next = exec.phases.find((p: PhaseExecution) => p.status !== "qa_passed");
  return next?.phaseName ?? exec.phases[exec.phases.length - 1]?.phaseName ?? "—";
}

export function getResumableChecklists(args: {
  jobs: Job[];
  specExecutions: JobSpecExecution[];
  matchKey: string | null | undefined;
  todayIso: string;
}): ResumableChecklist[] {
  const { jobs, specExecutions, matchKey, todayIso } = args;
  if (!matchKey) return [];
  const myJobs = filterJobsForContractor(jobs, matchKey);
  const out: ResumableChecklist[] = [];
  for (const job of myJobs) {
    if (job.scheduledInstallDate !== todayIso) continue;
    const exec = specExecutions.find((e) => e.jobId === job.id);
    if (!exec) continue;
    if (exec.allPhasesComplete) continue;
    out.push({
      jobId: job.id,
      customerName: job.customerName,
      addressCity: job.address?.city ?? "",
      currentPhase: deriveCurrentPhase(exec),
      href: `/installer-execution/${job.id}`,
    });
  }
  return out;
}
